import { useState } from 'react';
import { Plus, Check } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { SECTION_METADATA } from '@/config/sectionMetadata';
import type { AddonSection } from '@/config/sectionMetadata';
import { useSectionConfig, readOverrides, computeNextOverrides } from '@/hooks/useSectionConfig';
import type { Project } from '@/lib/schemas';

interface AddModuleAffordanceProps {
  project: Project;
  /** Add-in section keys that are currently hidden and can be enabled. */
  hiddenAddIns: string[];
  /**
   * Layer-1-through-4 visibility per add-in (no per-project overrides). Used to
   * keep the overrides map clean: showing a section whose default stack already
   * shows it CLEARS the key instead of pinning a stale `true`.
   */
  defaultStackVisibility: Record<AddonSection, boolean>;
}

/**
 * R3: "+ Add section" affordance rendered at the bottom of the section list.
 * Visible only when hiddenAddIns.length > 0.
 * Opens a picker modal listing hidden add-ins with labels/descriptions from SECTION_METADATA.
 */
export function AddModuleAffordance({ project, hiddenAddIns, defaultStackVisibility }: AddModuleAffordanceProps) {
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const mutation = useSectionConfig();

  if (hiddenAddIns.length === 0) return null;

  function toggleSection(key: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  }

  async function handleAdd() {
    if (selected.size === 0) return;

    // Show-semantics with override hygiene: derive the next map from the project's
    // CURRENT overrides, clearing keys whose default stack already shows the section.
    let nextOverrides = readOverrides(project);
    for (const key of selected) {
      nextOverrides = computeNextOverrides(nextOverrides, key, true, defaultStackVisibility);
    }

    try {
      await mutation.mutateAsync({ project, overrides: nextOverrides });

      const labels = [...selected]
        .map((k) => SECTION_METADATA[k]?.label ?? k)
        .join(', ');
      toast.success(`${labels} added to this project`);

      setSelected(new Set());
      setOpen(false);
    } catch {
      toast.error('Failed to add section. Please try again.');
    }
  }

  function handleOpenChange(value: boolean) {
    if (!value) setSelected(new Set());
    setOpen(value);
  }

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors mt-2 px-1 py-1"
        data-testid="add-module-affordance"
      >
        <Plus className="w-3.5 h-3.5" />
        Add section
      </button>

      <Dialog open={open} onOpenChange={handleOpenChange}>
        <DialogContent className="sm:max-w-[480px]" data-testid="add-module-modal">
          <DialogHeader>
            <DialogTitle>Add a section</DialogTitle>
            <DialogDescription>
              Select one or more sections to add to this project.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-1 my-2" data-testid="add-module-list">
            {hiddenAddIns.map((key) => {
              const meta = SECTION_METADATA[key];
              if (!meta) return null;
              const isSelected = selected.has(key);

              return (
                <button
                  key={key}
                  onClick={() => toggleSection(key)}
                  className={`w-full flex items-start gap-3 rounded-md px-3 py-2.5 text-left transition-colors ${
                    isSelected
                      ? 'bg-primary/10 border border-primary/20'
                      : 'hover:bg-muted border border-transparent'
                  }`}
                  data-testid={`add-module-option-${key}`}
                >
                  <div className={`mt-0.5 flex-shrink-0 w-4 h-4 rounded border flex items-center justify-center ${
                    isSelected ? 'bg-primary border-primary' : 'border-muted-foreground/40'
                  }`}>
                    {isSelected && <Check className="w-2.5 h-2.5 text-primary-foreground" />}
                  </div>
                  <div>
                    <div className="text-sm font-medium">{meta.label}</div>
                    <div className="text-xs text-muted-foreground">{meta.description}</div>
                  </div>
                </button>
              );
            })}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => handleOpenChange(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleAdd}
              disabled={selected.size === 0 || mutation.isPending}
              data-testid="add-module-submit"
            >
              {mutation.isPending ? 'Adding…' : `Add${selected.size > 0 ? ` (${selected.size})` : ''}`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
